import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import logoImg from '../../assets/logo.png';
import { logout, useAuth } from '../../auth/services/authService';
import { checkSellerByUserId } from '../../auth/services/firestoreService';
import { checkIsAdmin } from '../../admin/services/adminService';
import toast from 'react-hot-toast';
import { 
  LayoutDashboard, 
  ShoppingBag, 
  Package, 
  Video, 
  IndianRupee, 
  Wallet, 
  Settings, 
  LogOut,
  CheckCircle2,
  PlusCircle,
  Download,
  Menu,
  X,
  Instagram,
  ShieldCheck,
  Radio
} from 'lucide-react';

const Sidebar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);

  useEffect(() => {
    const verifyAdmin = async () => {
      if (user) {
        try {
          const { seller } = await checkSellerByUserId(user.uid);
          const adminCheck = checkIsAdmin(user, seller);
          setIsAdminUser(adminCheck);
        } catch (err) {
          console.error("Error verifying admin in sidebar:", err);
        }
      }
    };
    verifyAdmin();
  }, [user]);

  const handleLogout = async () => {
    try {
      const { success, error } = await logout();
      if (success) {
        toast.success('Logged out successfully');
        navigate('/auth/login');
      } else {
        toast.error(error || 'Failed to logout');
      }
    } catch (err) {
      console.error('Logout error:', err);
      toast.error('Failed to logout');
    }
  };

  const navItems = [
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { name: 'Live Drops', path: '/live-drops', icon: Radio, isLiveBadge: true },
    { name: 'Orders', path: '/orders', icon: ShoppingBag },
    { name: 'Inventory', path: '/products', icon: Package },
    { name: 'Add Product', path: '/products/add', icon: PlusCircle },
    { name: 'Import Products', path: '/products/import', icon: Download },
    { name: 'Videos', path: '/videos', icon: Video },
    { name: 'Instagram', path: '/instagram', icon: Instagram },
    { name: 'Earnings', path: '/earnings', icon: Wallet },
    { name: 'Settings', path: '/profile', icon: Settings },
  ];


  if (isAdminUser) {
    // Insert Admin Panel at top of nav items
    navItems.unshift({ 
      name: 'Admin Panel', 
      path: '/admin', 
      icon: ShieldCheck, 
      isAdminBadge: true 
    });
  }

  // Configure Mobile Bottom Nav Items
  const mobileNavItems = [
    navItems.find(i => i.path === '/dashboard') || navItems[0],
    navItems.find(i => i.path === '/orders') || navItems[1],
    navItems.find(i => i.path === '/products/add') || navItems[3],
    navItems.find(i => i.path === '/products') || navItems[2],
  ];

  const menuItems = navItems.filter(i => 
    !mobileNavItems.some(m => m.path === i.path)
  );

  return (
    <>
      {/* DESKTOP SIDEBAR */}
      <aside className="hidden md:flex flex-col w-64 bg-[#0a0b10] border-r border-white/5 min-h-screen fixed left-0 top-0 z-50 transition-all duration-300">
        {/* 1. Logo */}
        <div className="h-16 flex items-center px-6 border-b border-white/5">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
             <img src={logoImg} alt="Reelkart" className="w-8 h-8 object-contain" />
             <span className="text-[22px] font-bold tracking-tight text-white flex items-center gap-1.5">
               Reelkart
               <span className="text-[9px] bg-[#FF0057] text-white font-extrabold px-1 rounded uppercase tracking-wider">Seller</span>
             </span>
          </div>
        </div>
        
        {/* 2. Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-hide">
          {navItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.path}
              end={item.path === '/products'}
              className={({ isActive }) =>
                `flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
                  isActive 
                    ? item.isAdminBadge 
                      ? 'bg-gradient-to-r from-[#FF0057]/20 to-purple-600/20 text-[#FF0057] border border-[#FF0057]/40' 
                      : 'bg-[#FF0057]/10 text-[#FF0057]' 
                    : item.isAdminBadge
                      ? 'text-[#FF0057] bg-[#FF0057]/5 hover:bg-[#FF0057]/15 border border-[#FF0057]/20'
                      : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`
              }
            >
              {({ isActive }) => (
                  <>
                    <div className="flex items-center gap-3">
                      <item.icon size={20} className={`transition-colors ${
                        item.isAdminBadge ? 'text-[#FF0057]' : item.isLiveBadge ? 'text-red-500 animate-pulse' : isActive ? 'text-[#FF0057]' : 'text-gray-400 group-hover:text-gray-300'
                      }`} />
                      <span>{item.name}</span>
                    </div>
                    {item.isAdminBadge && (
                      <span className="text-[9px] bg-[#FF0057] text-white font-black px-1.5 py-0.5 rounded uppercase tracking-wider shadow-[0_0_8px_rgba(255,0,87,0.4)]">
                        ADMIN
                      </span>
                    )}
                    {item.isLiveBadge && (
                      <span className="text-[9px] bg-gradient-to-r from-red-600 to-pink-600 text-white font-black px-1.5 py-0.5 rounded uppercase tracking-wider shadow-[0_0_8px_rgba(239,68,68,0.4)] flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping"></span>
                        LIVE
                      </span>
                    )}
                  </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* 3. Footer Actions */}
        <div className="p-4 border-t border-white/5">
          <button 
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2.5 w-full rounded-xl text-sm font-medium text-gray-400 hover:bg-red-950/20 hover:text-red-500 transition-all group"
          >
              <LogOut size={20} className="text-gray-400 group-hover:text-red-500 transition-colors" />
              Logout
          </button>
        </div>
      </aside>

      {/* MOBILE MENU DRAWER */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[60]">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={() => setMobileMenuOpen(false)}
          ></div>
          
          {/* Drawer Content */}
          <div className="absolute bottom-16 right-0 left-0 bg-[#0a0b10] rounded-t-3xl p-6 animate-slide-up shadow-2xl border-t border-white/10 max-h-[80vh] overflow-y-auto">
             <div className="flex items-center justify-between mb-6">
               <h3 className="text-lg font-bold text-white">Menu</h3>
               <button 
                 onClick={() => setMobileMenuOpen(false)}
                 className="p-2 bg-white/5 rounded-full text-gray-400 hover:bg-white/10 hover:text-white"
               >
                 <X size={20} />
               </button>
             </div>

             <div className="grid grid-cols-3 gap-3 mb-6">
                 {menuItems.map((item) => (
                    <NavLink
                       key={item.name}
                       to={item.path}
                       onClick={() => setMobileMenuOpen(false)}
                       className={({ isActive }) =>
                         `flex flex-col items-center gap-2 p-3 rounded-xl transition-all ${
                           isActive 
                             ? 'bg-[#FF0057]/10 text-[#FF0057]' 
                             : item.isAdminBadge
                               ? 'bg-[#FF0057]/15 text-[#FF0057] border border-[#FF0057]/30'
                               : 'bg-white/5 text-gray-400 hover:bg-white/10'
                         }`
                       }
                    >
                       <item.icon size={24} />
                       <span className="text-xs font-medium text-center">{item.name}</span>
                    </NavLink>
                 ))}
             </div>
             
             <button 
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                className="w-full flex items-center justify-center gap-2 p-4 bg-red-950/20 text-red-400 border border-red-500/10 rounded-xl font-bold hover:bg-red-950/40 transition-colors"
             >
                <LogOut size={20} />
                Logout
             </button>
          </div>
        </div>
      )}

      {/* MOBILE BOTTOM NAV */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-[#0a0b10]/95 backdrop-blur-xl border-t border-white/5 z-50 pb-safe">
        <div className="flex justify-around items-center h-16 px-2 relative">
          
          {/* First 2 Items */}
          {mobileNavItems.slice(0, 2).map((item) => (
             <NavLink
                key={item.name}
                to={item.path}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center w-full h-full space-y-1 ${
                    isActive ? 'text-[#FF0057]' : 'text-gray-400'
                  }`
                }
             >
                <item.icon size={20} />
                <span className="text-[10px] font-medium">{item.name}</span>
             </NavLink>
          ))}

          {/* Middle Item: Add Product */}
          <NavLink
             to="/products/add"
             className={({ isActive }) =>
               `flex flex-col items-center justify-center w-full h-full space-y-1 -mt-6 ${
                 isActive ? 'text-[#FF0057]' : 'text-gray-400'
               }`
             }
          >
             <div className="w-12 h-12 bg-[#FF0057] rounded-full shadow-lg shadow-[#FF0057]/20 flex items-center justify-center text-white mb-1 transform transition-transform active:scale-95">
                <PlusCircle size={24} />
             </div>
             <span className="text-[10px] font-bold text-gray-400">Add</span>
          </NavLink>

          {/* 4th Item */}
          {mobileNavItems.slice(3, 4).map((item) => (
             <NavLink
                key={item.name}
                to={item.path}
                end={item.path === '/products'}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center w-full h-full space-y-1 ${
                    isActive ? 'text-[#FF0057]' : 'text-gray-400'
                  }`
                }
             >
                <item.icon size={20} />
                <span className="text-[10px] font-medium">{item.name}</span>
             </NavLink>
          ))}

           {/* 5th Item: Menu Toggle */}
           <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`flex flex-col items-center justify-center w-full h-full space-y-1 ${
                 mobileMenuOpen ? 'text-[#FF0057]' : 'text-gray-400'
              }`}
           >
              <Menu size={20} />
              <span className="text-[10px] font-medium">Menu</span>
           </button>

        </div>
      </div>
    </>
  );
};

export default Sidebar;
